import { CssBaseline, ThemeProvider } from '@mui/material';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { Provider } from 'jotai';
import {
  ApiError,
  AppNameProvider,
  ErrorFallback,
  Forbidden,
  Policy,
} from 'ui';
import { APP_NAME } from '../appName';
import '../sentry.client.config';
import '../styles/Home.module.css';
import theme from '../styles/theme';
import useUser from '../auth/useUser';
import { ErrorBoundary } from 'react-error-boundary';
import dynamic from 'next/dynamic';
import { AppProps } from 'next/app';
import toast, { Toaster } from 'react-hot-toast';
import React from 'react';
import { getVersion } from '../utils/version';

// AuthContextProvider depende de window, así que lo cargamos sin SSR.
const AuthContextProvider = dynamic(
  () => {
    return import('../auth/AuthContext');
  },
  { ssr: false }
);

const errorFunction = (error: unknown) => {
  if (error instanceof ApiError) {
    console.error(error, {
      errorCode: error.errorCode,
      appName: error.appName,
      ctx: error.ctx,
      cause: error.cause,
      requestId: error.requestId,
      errorId: error.errorId,
    });
    toast.error(
      `Error: ${error.message}\nCódigo: ${error.errorCode}\n[${error.errorId}]`,
      {
        duration: 5000,
      }
    );
    return;
  }

  let realError = error;
  if (!(error instanceof Error)) {
    realError = new Error(JSON.stringify(error));
  }

  console.error(realError);
  toast.error(`Error: ${(realError as Error).message}`);
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: errorFunction,
  }),
  mutationCache: new MutationCache({
    onError: errorFunction,
  }),
  defaultOptions: {
    queries: {
      retry: 3,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

type ExtendedAppProps = AppProps & {
  Component: AppProps['Component'] & { skipAuth?: boolean };
};

function AppLayout({ Component, pageProps }: ExtendedAppProps) {
  const user = useUser();

  return (
    <Policy
      user={user}
      roles={['login']}
      fallback={
        <Forbidden
          text={'No tienes permiso para acceder a la aplicación'}
          userLogout={user?.actions?.logout}
          email={user?.email}
        />
      }
    >
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Component {...pageProps} />
      </ThemeProvider>
    </Policy>
  );
}

function ComponentWrapper(props: ExtendedAppProps) {
  const { Component, pageProps } = props;
  if (Component.skipAuth) {
    return (
      <React.StrictMode>
        <Component {...pageProps} />
      </React.StrictMode>
    );
  }

  // AuthContextProvider (y keycloak-js) no juega bien con StrictMode: provoca
  // un loop de redirección al volver del IdP. Por eso queda fuera de StrictMode.
  return (
    <AuthContextProvider clientId="monorepo">
      <AppLayout {...props} />
    </AuthContextProvider>
  );
}

function MyApp(props: ExtendedAppProps) {
  console.log('Version', getVersion());

  return (
    <ErrorBoundary FallbackComponent={() => <ErrorFallback />}>
      <AppNameProvider appName={APP_NAME}>
        <Provider>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 3000,
              style: {
                background: '#fff',
                color: '#1f2937',
                borderRadius: 8,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              },
            }}
          />
          <QueryClientProvider client={queryClient}>
            <ComponentWrapper {...props} />
          </QueryClientProvider>
        </Provider>
      </AppNameProvider>
    </ErrorBoundary>
  );
}

export default MyApp;
